import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import { FC } from 'react';
import { Colors, Typographies } from '../../../theme';
import { Spacing } from '../../../utils';
import { Icon, Icons } from '../../atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../atoms/containerV2/ContainerV2';
import { StyledTabs, StyledTypography, TabAlignment } from './DynamicTab.styles';

export type IconPositionType = 'start' | 'top' | 'bottom' | 'end';

export type TabLabelProps = {
  tabName: string;
  isTabDisabled?: boolean;
  isTabVisible?: boolean;
  icon?: React.ReactElement | string;
  iconPosition?: IconPositionType;
  labelMargin?: Spacing;
  labelType?: Typographies;
  isTabLoading?: boolean;
};

export type TabContentProps = {
  tabContent: JSX.Element;
  visible?: boolean;
};

export type DynamicTabProps = {
  tabLabelArray: TabLabelProps[];
  content: TabContentProps[];
  tabValue: number;
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  isCentered?: boolean;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  alignment?: TabAlignment | undefined;
  selectedLabelColor?: Colors;
  tabBgColor?: Colors;
  fullHeight?: boolean;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  if (value !== index) return null;

  return (
    <ColumnFlexContainer role="tabpanel" ria-labelledby={`simple-tab-${index}`} flex={1}>
      {value === index && children}
    </ColumnFlexContainer>
  );
};

export const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
  value: index,
});

export const DynamicTab: FC<DynamicTabProps> = ({
  tabLabelArray,
  content,
  tabValue,
  handleTabChange,
  isCentered = false,
  variant = 'standard',
  alignment,
  selectedLabelColor = 'primary',
  tabBgColor = 'white',
  fullHeight = false,
}) => {
  return (
    <Box
      data-testid="tab-wrapper"
      flex={fullHeight ? 1 : undefined}
      display={fullHeight ? 'flex' : 'inherit'}
      flexDirection={'column'}
    >
      <StyledTabs
        data-testid="tabs-container"
        value={tabValue}
        onChange={handleTabChange}
        centered={isCentered}
        variant={variant}
        alignment={alignment}
        scrollButtons={false}
        tabbackgroundcolor={tabBgColor}
      >
        {tabLabelArray.map(
          (tabLabel, index) =>
            tabLabel.isTabVisible && (
              <Tab
                key={`tab-${index}`}
                data-testid="tab"
                label={
                  <RowFlexContainer>
                    <StyledTypography
                      margin={tabLabel.labelMargin}
                      type={tabLabel.labelType}
                      color={tabValue === index ? selectedLabelColor : 'primary'}
                    >
                      {tabLabel.tabName}
                    </StyledTypography>
                    {tabLabel.isTabLoading && (
                      <Icon data-testid="spinner" icon={Icons.SPINNER} margin={[0, 2, 0]} size="small" />
                    )}
                  </RowFlexContainer>
                }
                disabled={tabLabel.isTabDisabled}
                icon={tabLabel.icon}
                iconPosition={tabLabel.iconPosition}
                {...a11yProps(index)}
              />
            ),
        )}
      </StyledTabs>
      <ColumnFlexContainer data-testid="tab-panel-container" flex={fullHeight ? 1 : undefined}>
        {content.map(
          (itemValue, index) =>
            itemValue.visible && (
              <TabPanel data-testid="tab-panel" key={index} value={tabValue} index={index}>
                {itemValue.tabContent}
              </TabPanel>
            ),
        )}
      </ColumnFlexContainer>
    </Box>
  );
};
